<script setup lang="ts">
import {
  BoxComponent,
  ButtonComponent,
  COMMON_RESET_PAGE_ON,
  commonFilterFields,
  enumField,
  FlexComponent,
  LoadingComponent,
  PageWrapperComponent,
  PaginationComponent,
  SegmentListItem,
  type SegmentsFilter,
  TypographyComponent,
  useQueryFilter,
} from '@metapic-apps/shared'
import { ElIcon } from 'element-plus'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { useGetSegments } from '@/api/segments.api'
import PlusIcon from '@/assets/icons/add-plus.svg?component'
import { SegmentFilters } from '@/components/Segments'
import { useAdvertiserStore } from '@/stores/advertiser.stores'

const router = useRouter()
const advertiserStore = useAdvertiserStore()
const advertiser = computed(() => advertiserStore?.advertiser)
const { filter } = useQueryFilter<SegmentsFilter>(
  {
    page: commonFilterFields.page,
    query: commonFilterFields.query,
    sortBy: commonFilterFields.sortBy,
    type: enumField('type', ['static', 'dynamic']),
    createdByMe: commonFilterFields.createdByMe,
  },
  {
    page: 1,
    sortBy: 'updated_at:desc',
  },
  { resetPageOn: [...COMMON_RESET_PAGE_ON, 'type'] },
)
const { data: segments, totalPages, isLoading } = useGetSegments(filter, advertiser)
const page = computed({
  get: () => filter.value.page ?? 1,
  set: (value: number) => {
    filter.value = { ...filter.value, page: value }
  },
})
const isEmpty = computed(() => !isLoading.value && segments.value.length === 0)

const handleCreate = () => router.push({ name: 'segment-create' })
const handleEdit = (id: string) => router.push({ name: 'segment-edit', params: { id } })
</script>
<template>
  <BoxComponent :pl="24" :pr="24">
    <main>
      <PageWrapperComponent>
        <BoxComponent :mb="32">
          <SegmentFilters v-model="filter">
            <template #actions>
              <ButtonComponent variant="accent" class="segments__create" @click="handleCreate">
                <template #iconLeft>
                  <ElIcon :size="18">
                    <PlusIcon />
                  </ElIcon>
                </template>
                Create segment
              </ButtonComponent>
            </template>
          </SegmentFilters>
        </BoxComponent>
        <BoxComponent v-if="isLoading" class="segments__feedback">
          <LoadingComponent />
        </BoxComponent>
        <BoxComponent v-else-if="isEmpty" class="segments__feedback segments__empty">
          <TypographyComponent variant="title-medium">No segments match your filters</TypographyComponent>
        </BoxComponent>
        <FlexComponent v-else direction="column" :gap="8" class="segments-list">
          <button
            v-for="segment in segments"
            :key="segment.id"
            type="button"
            class="segments__item"
            :aria-label="`Edit segment ${segment.name}`"
            @click="handleEdit(segment.id)"
          >
            <SegmentListItem :segment="segment" />
          </button>
        </FlexComponent>
        <BoxComponent v-if="totalPages > 1" :mt="24">
          <PaginationComponent v-model:current-page="page" :total-pages="totalPages" />
        </BoxComponent>
      </PageWrapperComponent>
    </main>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.segments__item {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: inherit;
  cursor: pointer;
}
.segments__feedback {
  padding: 48px;
  text-align: center;
}
.segments__create.segments__create {
  :deep(svg) {
    fill: currentColor;
  }
}
</style>
